import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { BehaviorSubject, Observable, catchError, map, take } from 'rxjs';
import { ResultFilterModel } from '../shared/models/result-filter.model';
import { ServiceBase } from './servicebase.service';

@Injectable({
  providedIn: 'root'
})
export class ResultsService extends ServiceBase {

  prosecutionTypesSubj: BehaviorSubject<ServerModels.ENESS_REF_SuccessStoryProsecutionTypes[]> = new BehaviorSubject([]);
  prosecutionTypes$: Observable<ServerModels.ENESS_REF_SuccessStoryProsecutionTypes[]> = this.prosecutionTypesSubj.asObservable();

  adjudicationStatusesSubj: BehaviorSubject<ServerModels.ENESS_REF_SuccessStoryAdjudicationStatuses[]> = new BehaviorSubject([]);
  adjudicationStatuses$: Observable<ServerModels.ENESS_REF_SuccessStoryAdjudicationStatuses[]> = this.adjudicationStatusesSubj.asObservable();

  constructor(protected http: HttpClient,
    public messageService: MessageService) {
    super(http, messageService);
    this.getProsecutionTypesInternal();
    this.getAdjudicationStatuses();
  }

  public addResult(result: ServerModels.ENESS_SuccessStory): Observable<ServerModels.ResultDetailViewModel> {
    return this.http.post<ServerModels.ResultDetailViewModel>(`${this.apiRoot}/api/Result/AddOrUpdateResult`, result, this.httpOptions)
      .pipe(catchError(this.throwError<any>('addResult', 'defaultMsg', null, null)));
  }

  public deleteResultAttachments(attachments: ServerModels.ENESS_SuccessStoryAttachments): Observable<ServerModels.ENESS_SuccessStoryAttachments> {
    return this.http.post<ServerModels.ENESS_SuccessStoryAttachments>(`${this.apiRoot}/api/Result/DeleteResultAttachments`, attachments, this.httpOptions)
      .pipe(catchError(this.swallowError<any>('deleteResultAttachments', 'defaultMsg', null, null))); 
  }

  public getAttachmentById(resultId: string, attachmentId: string): Observable<Blob> {
    let options = {
      responseType: 'blob' as 'json',
      params: {
        resultId: resultId,
        attachmentId: attachmentId
      }
    };
    return this.http.get<Blob>(`${this.apiRoot}/api/Result/GetAttachmentById`, options)
      .pipe(catchError(this.throwError<any>('getAttachmentById', 'defaultMsg', null, null)));
  }

  public getFilters(): Observable<ServerModels.ENESS_REF_SuccessStoryFilters[]> {
    return this.http.get<ServerModels.ENESS_REF_SuccessStoryFilters[]>(`${this.apiRoot}/api/Result/GetResultFilters`, this.httpOptions)
      .pipe(catchError(this.throwError<any>('getFilters', 'defaultMsg', null, null)));
  }

  public getJudicialDistricts(): Observable<ServerModels.ENESS_REF_JudicialDistricts[]> {
    return this.http.get<ServerModels.ENESS_REF_JudicialDistricts[]>(`${this.apiRoot}/api/Result/GetJudicialDistricts`, this.httpOptions)
      .pipe(catchError(this.swallowError<any>('getJudicialDistricts', 'defaultMsg', null, null)));
  }

  private getProsecutionTypesInternal() {
    this.http.get<ServerModels.ENESS_REF_SuccessStoryProsecutionTypes[]>(`${this.apiRoot}/api/Result/GetProsecutionTypes`, this.httpOptions)
      .pipe(
        take(1),
        map((types: ServerModels.ENESS_REF_SuccessStoryProsecutionTypes[]) => this.prosecutionTypesSubj.next(types)),
        catchError(this.swallowError<any>('getProsecutionTypesInternal', 'defaultMsg', null, null))
    ).subscribe();
  }

  public getAdjudicationStatuses() {
    this.http.get<ServerModels.ENESS_REF_SuccessStoryAdjudicationStatuses[]>(`${this.apiRoot}/api/Result/GetAdjudicationStatuses`, this.httpOptions)
      .pipe(
        take(1),
        map((statuses: ServerModels.ENESS_REF_SuccessStoryAdjudicationStatuses[]) => this.adjudicationStatusesSubj.next(statuses)),
        catchError(this.swallowError<any>('getAdjudicationStatuses', 'defaultMsg', null, null)) 
      ).subscribe();
  }

  public getProsecutionTypes(): Observable<ServerModels.ENESS_REF_SuccessStoryProsecutionTypes[]> {
    return this.http.get<ServerModels.ENESS_REF_SuccessStoryProsecutionTypes[]>(`${this.apiRoot}/api/Result/GetProsecutionTypes`, this.httpOptions)
      .pipe(catchError(this.swallowError<any>('getProsecutionTypes', 'defaultMsg', null, null))); 
  }

  public getResultById(resultId: string): Observable<ServerModels.ResultDetailViewModel> {
    return this.http.get<ServerModels.ResultDetailViewModel>(`${this.apiRoot}/api/Result/GetResultByID?resultID=${resultId}`, this.httpOptions)
      .pipe(catchError(this.swallowError<any>('getResultById', 'defaultMsg', null, null))); 
  }

  public getResultDetailViewModel(): Observable<ServerModels.ResultDetailViewModel> {
    return this.http.get<ServerModels.ResultDetailViewModel>(`${this.apiRoot}/api/Result/GetResultDetailViewModel`, this.httpOptions)
      .pipe(catchError(this.swallowError<any>('getResultDetailViewModel', 'defaultMsg', null, null))); 
  }

  public getResults(pageNumber: number, filters: ResultFilterModel): Observable<ServerModels.ResultTableViewModel[]> {
    return this.http.get<ServerModels.ResultTableViewModel[]>(`${this.apiRoot}/api/Result/GetResultView`, {
      headers: this.httpOptions.headers,
      params: {
        'resultFilterID': filters.currentView,
        'pageNumber': pageNumber.toString(),
        'selectedCGICCode': filters.cgicCode
      }
    }).pipe(catchError(this.swallowError<any>('getResults', 'defaultMsg', null, null))); 
  }

  public downloadPrintablePdf(successStoryId: string): Observable<Blob> {
    let options = {
      responseType: this.httpBlob.responseType,
      headers: this.httpBlob.headers,
      params: {
        'successStoryId': successStoryId
      }
    };
    return this.http.get<Blob>(`${this.apiRoot}/api/Result/DownloadPdf`, options)
      .pipe(catchError(this.swallowError<any>('downloadPrintablePdf', 'defaultMsg', null, null))); 
  }


  public getCGICPersonnel(cgicCode: string): Observable<ServerModels.CGICPersonnelViewModel[]> {
    let options = {
      headers: this.httpOptions.headers,
      params: { 'cgicCode': cgicCode }
    };

    return this.http.get<ServerModels.CGICPersonnelViewModel[]>(`${this.apiRoot}/api/Result/GetAllCurrentCGICPersonnel`, options)
      .pipe(catchError(this.swallowError<any>('getCGICPersonnel', 'defaultMsg', null, null))); 
  }

  public getSupervisorsByCGIC(cgicCode: string): Observable<ServerModels.CGICPersonnelViewModel[]> {
    let options = {
      headers: this.httpOptions.headers,
      params: { 'cgicCode': cgicCode }
    };

    return this.http.get<ServerModels.SupervisorViewModel[]>(`${this.apiRoot}/api/Result/GetSupervisorsByCGIC`, options)
      .pipe(catchError(this.swallowError<any>('getSupervisorsByCGIC', 'defaultMsg', null, null))); 
  }
}
